/**
 * RecentBattlesSection Component
 *
 * Collapsible section displaying user's recent battle history with stats.
 * Shows W/L/D record in header and list of battles when expanded.
 */

import { useState } from 'react';
import { useBattleHistory } from '../../hooks/useBattleHistory';
import styles from './RecentBattlesSection.module.css';

/**
 * Format date to relative or short format
 */
function formatBattleDate(dateString: string | null): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    // Format as "Jan 15"
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

/**
 * Chevron icon component for expand/collapse indicator
 */
function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * Result badge component
 */
function ResultBadge({ result }: { result: 'win' | 'loss' | 'draw' }) {
  const badgeClass = {
    win: styles.badgeSuccess,
    loss: styles.badgeDanger,
    draw: styles.badgeWarning
  }[result];

  const label = result.charAt(0).toUpperCase() + result.slice(1);

  return <span className={`${styles.badge} ${badgeClass}`}>{label}</span>;
}

/**
 * RecentBattlesSection Component
 *
 * @param limit - Maximum number of battles to display (default: 5)
 *
 * @example
 * ```tsx
 * <RecentBattlesSection limit={10} />
 * ```
 */
export function RecentBattlesSection({ limit = 5 }: { limit?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { battles, stats, isLoading, error } = useBattleHistory(limit);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <div className={styles.container}>
      {/* Header - always visible */}
      <button
        className={styles.header}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-label={`Your Recent Battles - ${stats.wins}W / ${stats.losses}L / ${stats.draws}D`}
      >
        <div className={styles.headerLeft}>
          <ChevronIcon expanded={isExpanded} />
          <h3 className={styles.title}>Your Recent Battles</h3>
        </div>
        <div className={styles.stats}>
          <span className={styles.statWin}>{stats.wins}W</span>
          <span className={styles.statDivider}>/</span>
          <span className={styles.statLoss}>{stats.losses}L</span>
          <span className={styles.statDivider}>/</span>
          <span className={styles.statDraw}>{stats.draws}D</span>
        </div>
      </button>

      {/* Content - visible when expanded */}
      {isExpanded && (
        <div className={styles.content}>
          {/* Loading state */}
          {isLoading && (
            <div className={styles.loadingState}>
              <span className={styles.loadingSpinner}></span>
              Loading battle history...
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className={styles.errorState}>
              {error}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && battles.length === 0 && (
            <div className={styles.emptyState}>
              No battles yet. Start your first battle!
            </div>
          )}

          {/* Battle list */}
          {!isLoading && !error && battles.length > 0 && (
            <ul className={styles.battleList}>
              {battles.map((battle) => (
                <li key={battle.battleId} className={styles.battleItem}>
                  <div className={styles.battleInfo}>
                    <span className={styles.battleName}>{battle.battleName}</span>
                    <span className={styles.battleMeta}>
                      vs {battle.opponentDisplayName}
                    </span>
                  </div>
                  <div className={styles.battleResult}>
                    <ResultBadge result={battle.userResult} />
                    <span className={styles.battleDate}>
                      {formatBattleDate(battle.completedAt)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
